import { useSyncExternalStore } from 'react';

/**
 * Meta key controlling whether a query or mutation drives the top progress bar.
 * The bar is reserved for work the user is actively waiting on, and the two
 * kinds of work default accordingly:
 *
 * - Queries are EXCLUDED by default. Reads mostly happen in the background
 *   (enrichment lookups, status polls, cache warming) and each new one used to
 *   need its own suppress flag or the bar ran constantly during an agent run.
 *   Tag a query `meta: { [TOP_PROGRESS]: true }` only when its initial load is
 *   the page's content — the thing the user navigated to see. The agent
 *   surfaces (run list, agent record, transcript) are tagged today; other
 *   pages rely on their skeletons plus the route-Suspense bar, so tag their
 *   primary query when you touch one and its load deserves the bar.
 * - Mutations are INCLUDED by default: a mutation is normally a direct user
 *   action (sending a prompt, saving a persona). Tag
 *   `meta: { [TOP_PROGRESS]: false }` for background writes the user never
 *   awaits (autosaves, view recording) or long operations that own a dedicated
 *   in-context affordance (e.g. a plugin install's "Installing…" pill), so the
 *   bar doesn't flicker or appear stuck.
 */
export const TOP_PROGRESS = 'topProgress';

let routePending = 0;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

export function bumpRoutePending(delta: 1 | -1): void {
  routePending = Math.max(0, routePending + delta);
  emit();
}

export function getRoutePendingCount(): number {
  return routePending;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useRoutePending(): number {
  return useSyncExternalStore(subscribe, getRoutePendingCount, getRoutePendingCount);
}

export function __resetRoutePendingForTests(): void {
  routePending = 0;
  emit();
}
